import * as Yup from 'yup'
import { cloneDeep } from 'lodash-es'
import { ClientDetails } from './types'

export enum RULE_CONFIG_FORM_FIELDS {
  CLIENTID = 'clientId',
  OBJECTNAME = 'objectName',
  RULENAME = 'ruleName',
  ACTIVE = 'active'
}

export interface RuleConfigForm {
  [RULE_CONFIG_FORM_FIELDS.CLIENTID]: string;
  [RULE_CONFIG_FORM_FIELDS.OBJECTNAME]: string;
  [RULE_CONFIG_FORM_FIELDS.RULENAME]: string;
  [RULE_CONFIG_FORM_FIELDS.ACTIVE]: boolean;
}

export const defaultRuleConfigFormValues: RuleConfigForm = {
  [RULE_CONFIG_FORM_FIELDS.RULENAME]: '',
  [RULE_CONFIG_FORM_FIELDS.OBJECTNAME]: '',
  [RULE_CONFIG_FORM_FIELDS.CLIENTID]: '',
  [RULE_CONFIG_FORM_FIELDS.ACTIVE]: true
}

export const getRuleConfigValidationSchema = Yup.object().shape({
  [RULE_CONFIG_FORM_FIELDS.RULENAME]:
    Yup.string().required('Rule  is required'),
  [RULE_CONFIG_FORM_FIELDS.OBJECTNAME]: Yup.string()
    .required('Object Name is required')
    .min(5, 'Name must be at least 5 characters long')
    .max(50, 'Name must be at most 50 characters long')
})
export enum CLIENT_FORM_FIELDS {
  NAME = 'name',
  ACCOUNT_ID = 'accountId',
  CLIENT_ID = 'clientId',
  CLIENT_SECRET = 'clientSecret',
  USER_NAME = 'userName',
  PASSWORD = 'password',
  REFRESH_TOKEN = 'refreshToken',
  GRANT_TYPE = 'grantType',
  INTEGRATION_TYPE = 'integrationType',
  BASE_URL = 'baseUrl',
  TOKEN_EXPIRY = 'tokenExpiry',
  EXTERNAL_SYSTEM = 'externalSystem',
  REFERENCE_ID = 'referenceId',
  ACTIVE = 'active'
}

export interface ClientForm {
  [CLIENT_FORM_FIELDS.NAME]: string;
  [CLIENT_FORM_FIELDS.ACCOUNT_ID]?: string;
  [CLIENT_FORM_FIELDS.CLIENT_ID]?: string;
  [CLIENT_FORM_FIELDS.CLIENT_SECRET]?: string;
  [CLIENT_FORM_FIELDS.USER_NAME]?: string;
  [CLIENT_FORM_FIELDS.PASSWORD]?: string;
  [CLIENT_FORM_FIELDS.REFRESH_TOKEN]?: string;
  [CLIENT_FORM_FIELDS.GRANT_TYPE]: string;
  [CLIENT_FORM_FIELDS.INTEGRATION_TYPE]: string;
  [CLIENT_FORM_FIELDS.BASE_URL]: string;
  [CLIENT_FORM_FIELDS.TOKEN_EXPIRY]?: number;
  [CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM]?: string;
  [CLIENT_FORM_FIELDS.REFERENCE_ID]?: string;
  [CLIENT_FORM_FIELDS.ACTIVE]: boolean;
}



export const defaultClientFormValues: ClientForm = {
  [CLIENT_FORM_FIELDS.NAME]: '',
  [CLIENT_FORM_FIELDS.ACCOUNT_ID]: '',
  [CLIENT_FORM_FIELDS.CLIENT_ID]: '',
  [CLIENT_FORM_FIELDS.CLIENT_SECRET]: '',
  [CLIENT_FORM_FIELDS.USER_NAME]: '',
  [CLIENT_FORM_FIELDS.PASSWORD]: '',
  [CLIENT_FORM_FIELDS.REFRESH_TOKEN]: '',
  [CLIENT_FORM_FIELDS.GRANT_TYPE]: '',
  [CLIENT_FORM_FIELDS.INTEGRATION_TYPE]: '',
  [CLIENT_FORM_FIELDS.BASE_URL]: '',
  [CLIENT_FORM_FIELDS.TOKEN_EXPIRY]: undefined as unknown as number,
  [CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM]: '',
  [CLIENT_FORM_FIELDS.REFERENCE_ID]: '',
  [CLIENT_FORM_FIELDS.ACTIVE]: true
}

export const ClientFormLabels: Record<string, string> = {
  [CLIENT_FORM_FIELDS.NAME]: 'Name',
  [CLIENT_FORM_FIELDS.ACCOUNT_ID]: 'Account ID',
  [CLIENT_FORM_FIELDS.CLIENT_ID]: 'Client ID',
  [CLIENT_FORM_FIELDS.CLIENT_SECRET]: 'Client Secret',
  [CLIENT_FORM_FIELDS.USER_NAME]: 'User Name',
  [CLIENT_FORM_FIELDS.PASSWORD]: 'Password',
  [CLIENT_FORM_FIELDS.REFRESH_TOKEN]: 'Refresh Token',
  [CLIENT_FORM_FIELDS.GRANT_TYPE]: 'Grant Type',
  [CLIENT_FORM_FIELDS.INTEGRATION_TYPE]: 'Integration Type',
  [CLIENT_FORM_FIELDS.BASE_URL]: 'Base URL',
  [CLIENT_FORM_FIELDS.TOKEN_EXPIRY]: 'Token Expiry',
  [CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM]: 'External System',
  [CLIENT_FORM_FIELDS.REFERENCE_ID]: 'Reference ID',
  [CLIENT_FORM_FIELDS.ACTIVE]: 'Active'
}

export const getInitialClientFormValueFromResponse = (
  response: ClientDetails
): ClientForm => {
  const {
    name = '',
    accountId = '',
    clientId = '',
    clientSecret = '',
    userName = '',
    password = '',
    refreshToken = '',
    grantType = '',
    integrationType = '',
    baseUrl = '',
    tokenExpiry = undefined as unknown as number,
    externalSystem = '',
    referenceId = '',
    active = true
  } = response || {}

  const formInitialValues: ClientForm = {
    [CLIENT_FORM_FIELDS.NAME]: name,
    [CLIENT_FORM_FIELDS.ACCOUNT_ID]: accountId,
    [CLIENT_FORM_FIELDS.CLIENT_ID]: clientId,
    [CLIENT_FORM_FIELDS.CLIENT_SECRET]: clientSecret,
    [CLIENT_FORM_FIELDS.USER_NAME]: userName,
    [CLIENT_FORM_FIELDS.PASSWORD]: password,
    [CLIENT_FORM_FIELDS.REFRESH_TOKEN]: refreshToken,
    [CLIENT_FORM_FIELDS.GRANT_TYPE]: grantType,
    [CLIENT_FORM_FIELDS.INTEGRATION_TYPE]: integrationType,
    [CLIENT_FORM_FIELDS.BASE_URL]: baseUrl,
    [CLIENT_FORM_FIELDS.TOKEN_EXPIRY]: tokenExpiry,
    [CLIENT_FORM_FIELDS.EXTERNAL_SYSTEM]: externalSystem,
    [CLIENT_FORM_FIELDS.REFERENCE_ID]: referenceId,
    [CLIENT_FORM_FIELDS.ACTIVE]: Boolean(active)
  }

  return cloneDeep(formInitialValues)
}

export const getClientConfigValidationSchema = Yup.object().shape({
  [CLIENT_FORM_FIELDS.NAME]: Yup.string().required('Name is required'),
  [CLIENT_FORM_FIELDS.PASSWORD]: Yup.string()
    .test(
      'password-format',
      'Password must be at least 8 characters with letters and numbers',
      (value) => {
        if (!value) return true
        return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/.test(value)
      }
    )
    .notRequired(),
  [CLIENT_FORM_FIELDS.GRANT_TYPE]: Yup.string()
    .required('Grant Type is required'),
  [CLIENT_FORM_FIELDS.INTEGRATION_TYPE]: Yup.string()
    .required('Integration Type is required'),
  [CLIENT_FORM_FIELDS.BASE_URL]: Yup.string()
    .url('Invalid URL')
    .required('Base URL is required'),
  [CLIENT_FORM_FIELDS.TOKEN_EXPIRY]: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value
    )
    .typeError('Token Expiry must be a number')
    .notRequired(),
})
